import React, { useState, useEffect } from "react";
import axiosInstance from "../utils/axiosInstance";
import { useLocation } from "react-router-dom";
import { DeleteOutlined, CheckOutlined, TeamOutlined, FilterOutlined, SearchOutlined, MailOutlined, GlobalOutlined, CrownOutlined, UserOutlined } from "@ant-design/icons";

const RequestPage = () => {
  const [unapprovedUsers, setUnapprovedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Fetch unapproved users from the API
  const fetchUnapprovedUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }
      const response = await axiosInstance.get("/admin/users");
      if (response.data.success) {
        const users = (response.data.users || []).filter(
          (user) => user.isApproved === false
        );
        setUnapprovedUsers(users);
      } else {
        console.error("Failed to load users");
      }
    } catch (error) {
      console.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnapprovedUsers();
  }, []);

  // If a new company registration is passed via navigation state,
  // transform it into a pending user record and show only that record.
  const newCompany = location.state?.newCompany;
  let combinedUsers = unapprovedUsers;
  if (newCompany) {
    const transformedCompany = {
      id: newCompany.id,
      first_name: newCompany.first_name,
      last_name: newCompany.last_name,
      email: newCompany.email,
      phone_number: newCompany.phone_number || "",
      company_name: newCompany.company_name,
      country: "",
      role: "Company Registration",
    };
    combinedUsers = [transformedCompany];
  }

  // Approve user
  const handleSaveAndApprove = async (user) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }
      const payload = {
        isApproved: true,
      };
      const response = await axiosInstance.patch(
        `/admin/users/status/${user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        console.log("User updated and approved successfully!");
        setUnapprovedUsers((prevUsers) =>
          prevUsers.filter((u) => u.id !== user.id)
        );
      } else {
        console.error("Failed to update and approve user.");
      }
    } catch (error) {
      console.error("Failed to update and approve user.");
    }
  };

  // Delete a user from backend
  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }
      const response = await axiosInstance.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        console.log("User deleted successfully!");
        setUnapprovedUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== userId)
        );
      } else {
        console.error("Failed to delete user.");
      }
    } catch (error) {
      console.error("Failed to delete user.");
    }
  };

  // Delete confirmation
  const handleDeleteUserWithConfirm = (userId) => {
    if (window.confirm("Are you sure you want to delete? This action cannot be undone.")) {
      handleDeleteUser(userId);
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Role colors and icons
  const roleColors = {
    "Company Registration": "purple",
    "customer": "blue",
    "admin": "orange",
    "super admin": "red",
  };

  const roleIcons = {
    "Company Registration": <UserOutlined />,
    "customer": <UserOutlined />,
    "admin": <CrownOutlined />,
    "super admin": <CrownOutlined />,
  };

  const capitalizeRole = (role) => {
    if (!role) return "";
    return role
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl shadow-sm">
                <TeamOutlined className="text-blue-600 text-xl" />
              </div>
              Approve Users
            </h1>
            <p className="text-gray-600 mt-2">Manage and monitor all user approval requests</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-gray-600">Pending:</span>
                <span className="font-semibold text-gray-900">{combinedUsers.length} requests</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search Filter Card */}
        <div className="bg-white border-0 shadow-sm rounded-2xl overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <SearchOutlined className="text-gray-400" />
                </div>
              </div>
              <button className="px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                <FilterOutlined className="text-gray-400" />
                <span>Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Users Table Card */}
        <div className="bg-white border-0 shadow-sm rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approve
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {combinedUsers.map((user, index) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-500 font-mono text-sm">
                        #{(currentPage - 1) * pageSize + index + 1}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {getInitials(user.first_name + " " + user.last_name)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <MailOutlined className="text-gray-400" />
                            <span>{user.email}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.company_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.phone_number || 'Not provided'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold bg-${roleColors[user.role] || 'gray'}-100 text-${roleColors[user.role] || 'gray'}-800 border-0`}>
                        {roleIcons[user.role] || <UserOutlined />}
                        <span>{capitalizeRole(user.role)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleSaveAndApprove(user)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200"
                        title="Approve User"
                      >
                        <CheckOutlined className="text-xs" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDeleteUserWithConfirm(user.id)}
                        className="inline-flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
                        title="Delete"
                      >
                        <DeleteOutlined className="text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {combinedUsers.length > pageSize && (
            <div className="bg-white px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage * pageSize >= combinedUsers.length}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {Math.ceil(combinedUsers.length / pageSize)}
                </span>
              </div>
            </div>
          )}

          {/* Empty State */}
          {combinedUsers.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckOutlined className="text-2xl text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No pending approvals</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                All user registration requests have been approved. New requests will appear here automatically.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestPage;